import FlintTextBox from "@components//controls/FlintTextBox";
import * as icons from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styles from "./GridSearchBox.module.scss";
import React, {CSSProperties, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {CommonUtils} from "@utils/CommonUtils";
import NewGuid = CommonUtils.NewGuid;
import {InputHandle, InputPrefix, InputProps, TextBox, TextBoxHandle} from "@progress/kendo-react-inputs";
import {FloatingLabel} from "@progress/kendo-react-labels";

export default function GridSearchBox(props: GridSearchBoxProps) {
    const {state, handlers} = useGridSearchBox(props);
    const {t} = useTranslation();
    // Extract props that drive the wrapper (active / disabled) and the
    // controlled-value plumbing (value) BEFORE the spread so they don't reach
    // the underlying Kendo TextBox as unknown DOM attributes or conflict with
    // the explicit value+onChange wiring below.
    const {
        onSearch,
        active,
        disabled,
        value: _externalValue,
        placeholder = t("COMMON_SearchFieldLabel").toLowerCase(),
        label = t("COMMON_SearchFieldLabel").toLowerCase(),
        ...other
    } = props;

    const className = [
        styles.flintSearchBox,
        state.iconVisible.get ? "icon-visible" : "",
        active ? styles.flintSearchBoxActive : "",
        disabled ? styles.flintSearchBoxDisabled : "",
    ].filter(Boolean).join(" ");

    return (
        <div ref={state.wrapperRef} className={styles.flintSearchBoxWrapper}>
            <div className={className}>
                {state.iconVisible.get && <FontAwesomeIcon className={styles.flintSearchBoxIcon} size={"1x"} icon={icons.faSearch}/>}
                <FlintTextBox
                    {...other}
                    value={state.internalValue.get}
                    onChange={e => state.internalValue.set((e.value as string) ?? "")}
                    disabled={disabled}
                    label={label}
                    placeholder={label != null ? '' : placeholder}
                    onFocus={() => state.iconVisible.set(false)}
                    onBlur={e => handlers.handleSetSearch(e.currentTarget as HTMLInputElement)} type={"search"}
                    onEnterPressed={e => handlers.handleSetSearch(e.currentTarget as HTMLInputElement, false)}/>
            </div>
        </div>
    )
}

export interface GridSearchBoxProps extends InputProps {
    onSearch: (search: string) => void;
    /** When true, applies the orange "active filter" visual treatment (consumer
     *  should set this when the search value is non-empty). Purely presentational
     *  — the component remains stateless on value. */
    active?: boolean;
}

function useGridSearchBox(props: GridSearchBoxProps) {
    // Internal display value — seeded from props.value, then user typing drives
    // it directly. The component is controlled internally (so the user can type)
    // but only fires `onSearch` on blur / Enter / native ✕ clear (matches the
    // pre-persistence behaviour: don't search on every keystroke). External
    // prop changes (e.g. the consumer wiping quickFilter on advanced-apply)
    // sync to internal state via the useEffect below.
    const initialValue = (props.value as string | undefined) ?? "";
    const [internalValue, setInternalValue] = useState<string>(initialValue);

    // Initial icon visibility depends on whether the input is rehydrating with
    // a non-empty value (e.g. from sessionStorage via useSessionPersistedQuickSearch).
    // Without this, the search icon would overlap the persisted text on first
    // render; it only hides on focus today.
    const [iconVisible, setIconVisible] = useState<boolean>(
        () => initialValue.length === 0,
    );
    const wrapperRef = useRef<HTMLDivElement>(null);

    const currentValueRef = useRef<string>(initialValue);

    // Sync external prop changes (e.g. consumer wipes quickFilter after the
    // user applies an advanced filter) to internal state. Keeps the visible
    // text in lockstep with the consumer's idea of the value.
    useEffect(() => {
        const next = (props.value as string | undefined) ?? "";
        setInternalValue(next);
        currentValueRef.current = next;
        setIconVisible(next.length === 0);
    }, [props.value]);

    function handleSetSearch(tb: HTMLInputElement, verifyIconVisibility: boolean = true) {
        if (currentValueRef.current != tb.value) {
            props.onSearch(tb.value);
            currentValueRef.current = tb.value;
        }
        if (!verifyIconVisibility)
            return;
        let containsText = tb.value?.length > 0;
        setIconVisible(!containsText);
    }


    useEffect(() => {
        const wrapperElement = wrapperRef.current;
        if (!wrapperElement)
            return;
        const searchInput = wrapperElement.querySelector('input[type="search"]') as HTMLInputElement | null;
        if (!searchInput)
            return;
        currentValueRef.current = searchInput?.value;
        const handleSearch = (e: any) => {
            if (e.currentTarget.value)
                return;
            console.debug("=Grid filter reset=");
            currentValueRef.current = "";
            props.onSearch("");
        }
        searchInput.addEventListener("search", handleSearch);
        return () => {
            searchInput.removeEventListener("search", handleSearch);
        }
    }, [])
    return {
        state: {
            iconVisible: {get: iconVisible, set:setIconVisible},
            internalValue: {get: internalValue, set: setInternalValue},
            wrapperRef
        },
        handlers: {
            handleSetSearch
        }
    }
}